'use client'

import { AppTitle } from '@otomoro/components/shared/AppTitle'
import { Tag } from '@otomoro/components/shared/Tag'
import React, { HTMLAttributes, useRef } from 'react'
import { DateCardsVariants, markets } from './DateCards.css'
import { Card } from '@otomoro/components/shared/Cards/Card'
import { SanityImage } from '@otomoro/components/shared/SanityImage'
import { PortableText, PortableTextComponents } from 'next-sanity'
import { PortableTextObject } from 'sanity'
import { AppText } from '@otomoro/components/shared/AppText'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import clsx from 'clsx'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SectionHeader } from "@otomoro/components/shared/SectionHeader"

gsap.registerPlugin(ScrollTrigger)

interface DateCardsProps extends HTMLAttributes<Element>, DateCardsVariants {
	title: string
	headline: PortableTextObject
	description: PortableTextObject
	dateCards: any[]
}

export const DateCards: React.FC<DateCardsProps> = ({
	title,
	headline,
	description,
	dateCards,
}) => {
	const { base, container, stackContainer, image, card, figure } = markets()

	useGSAP(() => {
		const cards = gsap.utils.toArray('.cardDate') as HTMLDivElement[]
		const spacer = 20
		const minScale = 0.8

		const distributorScale = gsap.utils.distribute({ base: minScale, amount: 0.2 })

		cards.forEach((card, index) => {
			const scaleVal = distributorScale(index, cards[index], cards)

			const isLastCard = index === cards.length - 1
			if (!isLastCard) {
				gsap.to(card, {
					scrollTrigger: {
						trigger: card,
						start: `top top`,
						scrub: true,
						invalidateOnRefresh: true,
					},
					ease: 'none',
					scale: scaleVal,
					filter: 'blur(10px)',
					opacity: 0,
				})

				ScrollTrigger.create({
					trigger: card,
					start: `top-=125 top`,
					endTrigger: '.stackContainer',
					end: `bottom top-=${cards.length * spacer}`,
					pin: true,
					pinSpacing: false,
					id: `pin-${index}`,
					invalidateOnRefresh: true,
				})
			}
		})
	})

	return (
		<section className={base()}>
			<SectionHeader tag={title} title={headline} description={description} noPx />
			<div className={container()}>
				<div className={clsx(stackContainer(), 'stackContainer')}>
					{dateCards.map((dateCard, idx) => (
						<div key={idx} className={clsx(figure(), 'cardDate')}>
							<SanityImage
								figureClass={figure()}
								className={image()}
								image={dateCard?.image?.image as any}
								alt=""
								width={1920}
								height={1080}
							/>
							<Card
								date={dateCard?.date}
								theme="ternary"
								className={card()}
								title={dateCard?.title}
								body={dateCard?.description as any}
							/>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
